const ZERO_DECIMAL = new Set(["BIF", "CLP", "DJF", "GNF", "JPY", "KMF", "KRW", "MGA", "PYG", "RWF", "UGX", "VND", "VUV", "XAF", "XOF", "XPF"]);
const THREE_DECIMAL = new Set(["BHD", "JOD", "KWD", "OMR", "TND"]);

export const currencyDigits = (currency = "USD") => ZERO_DECIMAL.has(currency) ? 0 : THREE_DECIMAL.has(currency) ? 3 : 2;
export const majorAmount = (unitAmount, currency = "USD") => Number(unitAmount || 0) / (10 ** currencyDigits(currency));

export const billingKey = (price) => {
  const count = Math.max(1, Number(price?.intervalCount) || 1);
  const interval = String(price?.interval || "month");
  if (count === 1) return interval === "year" ? "annual" : interval === "month" ? "monthly" : interval === "week" ? "weekly" : "daily";
  return `${interval}_${count}`;
};

export const billingLabel = (price) => {
  const count = Math.max(1, Number(price?.intervalCount) || 1);
  const interval = String(price?.interval || "month");
  if (count === 1) return interval === "year" ? "Annual" : interval === "day" ? "Daily" : interval === "week" ? "Weekly" : "Monthly";
  return `Every ${count} ${interval}${count === 1 ? "" : "s"}`;
};

const fallbackPrice = (plan, key) => {
  if (key === "monthly") return { id: null, billingKey: key, label: "Monthly", interval: "month", intervalCount: 1, amount: plan.monthly, unitAmount: Math.round(plan.monthly * 100), currency: "USD" };
  return { id: null, billingKey: key, label: "Annual", interval: "year", intervalCount: 1, amount: plan.annualTotal, unitAmount: Math.round(plan.annualTotal * 100), currency: "USD" };
};

export const hydrateFallbackPlans = (templates) => templates.map((plan, index) => ({
  ...plan,
  comparisonIndex: index,
  stripeProductId: null,
  priceOptions: {
    monthly: fallbackPrice(plan, "monthly"),
    ...(plan.annualTotal ? { annual: fallbackPrice(plan, "annual") } : {}),
  },
}));

const normalized = (value) => String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const truthy = (value) => ["1", "true", "yes", "on"].includes(String(value || "").trim().toLowerCase());

export const plansFromCatalog = (catalog, templates) => {
  if (!Array.isArray(catalog?.products) || catalog.products.length === 0) return [];
  return catalog.products.map((product, index) => {
    const code = normalized(product.metadata?.plan_code);
    const name = normalized(product.name);
    const exact = templates.find((template) => [normalized(template.id), normalized(template.name)].includes(code || name));
    const template = exact || templates[Math.min(index, templates.length - 1)] || {};
    const prices = (product.prices || []).reduce((options, price) => {
      const currency = String(price.currency || "USD").toUpperCase();
      const key = billingKey(price);
      options[key] = { ...price, billingKey: key, label: billingLabel(price), currency, amount: majorAmount(price.unitAmount, currency) };
      return options;
    }, {});
    const onboarding = product.metadata?.onboarding_amount === undefined || product.metadata?.onboarding_amount === ""
      ? null
      : Number(product.metadata.onboarding_amount);
    return {
      ...template,
      id: product.metadata?.plan_code || product.id,
      stripeProductId: product.id,
      name: product.name,
      audience: product.metadata?.audience || product.description || template.audience || "A flexible Nailed subscription for construction teams.",
      popular: truthy(product.metadata?.popular),
      onboarding: Number.isFinite(onboarding) && onboarding >= 0 ? onboarding : null,
      limits: template.limits || [],
      features: product.features?.length ? product.features : (template.features || []),
      ctaLabel: product.metadata?.cta_label || template.ctaLabel || `Choose ${product.name}`,
      ctaEvent: template.ctaEvent || "plan_selected",
      comparisonIndex: exact ? templates.indexOf(exact) : Math.min(index, Math.max(0, templates.length - 1)),
      priceOptions: prices,
    };
  }).filter((plan) => Object.keys(plan.priceOptions).length > 0);
};

export const pricingPeriods = (plans) => {
  const keys = [];
  plans.forEach((plan) => Object.keys(plan.priceOptions || {}).forEach((key) => {
    if (!keys.includes(key)) keys.push(key);
  }));
  const order = { daily: 1, weekly: 2, monthly: 3, annual: 4 };
  return keys.sort((left, right) => (order[left] || 50) - (order[right] || 50));
};

export const priceForPlan = (plan, period) => plan?.priceOptions?.[period] || null;

export const pricePresentation = (price) => {
  if (!price) return null;
  if (price.interval === "year" && Number(price.intervalCount || 1) === 1) {
    return { amount: price.amount / 12, per: "/month", billed: `Billed ${formatCurrency(price.amount, price.currency)} annually` };
  }
  const count = Math.max(1, Number(price.intervalCount) || 1);
  const interval = String(price.interval || "month");
  return {
    amount: price.amount,
    per: count === 1 ? `/${interval}` : `/ ${count} ${interval}s`,
    billed: count === 1 ? `Billed ${interval}ly` : `Billed every ${count} ${interval}s`,
  };
};

export const formatCurrency = (amount, currency = "USD") => new Intl.NumberFormat("en-US", {
  style: "currency",
  currency,
  maximumFractionDigits: Number(amount) % 1 === 0 ? 0 : 2,
}).format(Number(amount) || 0);

const commercialSalesApiBase = "https://commercial.nailed.biz/api/v1";

export const resolveSalesApiBase = (configured) => {
  const value = String(configured || "").trim().replace(/\/$/, "");
  if (!value) return commercialSalesApiBase;

  try {
    const url = new URL(value);
    const hostname = url.hostname.toLowerCase();
    const isNailedHost = hostname === "nailed.biz" || hostname.endsWith(".nailed.biz");
    if (url.protocol !== "https:" || !isNailedHost) return commercialSalesApiBase;
    return url.pathname === "/" ? `${value}/api/v1` : value;
  } catch (_error) {
    return commercialSalesApiBase;
  }
};
